import React, { useState } from 'react';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { useAuth } from '../context/AuthContext';
import { addAreaHighlightOverlaysAndMessages } from './addAreaHighlightOverlaysUtil';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,

  Grid
} from '@mui/material';

// PLANETS list (remove Ketu as per user requirements)
const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter',
  'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'
];


function VedicAspectsStudyDialog({ open, onClose, barData, extendedBarData, onRun, onJournalAppend }) {
  // State for each row (all default to Mars as in screenshot)
  const [rows, setRows] = useState([
    { planet: 'Sun', type: 'nakshatraPlanet', value: 'Venus' },
    { planet: 'Sun', type: 'rashiPlanet', value: 'Venus' },
    { planet: 'Sun', type: 'drishtiPlanet', value: 'Venus' },
    { planet: 'Sun', type: 'nakshatraFriend', value: 'Venus' },
    { planet: 'Sun', type: 'nakshatraEnemy', value: 'Venus' },
    { planet: 'Jupiter', type: 'shadashatak', value: 'Saturn' },
    { planet: 'Jupiter', type: 'yog59', value: 'Venus' }
  ]);

  const { addOverlay } = useChartOverlays();
  const { api, isAdmin } = useAuth();

  // Color selection state
  const COLOR_OPTIONS = [
    { name: 'Red', value: 'rgba(229, 57, 53, 0.55)' },
    { name: 'Blue', value: 'rgba(30, 136, 229, 0.55)' },
    { name: 'Green', value: 'rgba(67, 160, 71, 0.55)' },
    { name: 'Orange', value: 'rgba(244, 81, 30, 0.55)' },
    { name: 'Purple', value: 'rgba(142, 36, 170, 0.55)' },
    { name: 'Yellow', value: 'rgba(255, 195, 0, 0.55)' },
    { name: 'Salmon', value: 'rgba(255, 87, 51, 0.55)' },
    { name: 'Dark Red', value: 'rgba(199, 0, 57, 0.55)' },
    { name: 'Pink', value: 'rgba(144, 12, 63, 0.55)' },
    { name: 'Violet', value: 'rgba(88, 24, 69, 0.55)' },
    { name: 'Light Green', value: 'rgba(218, 247, 166, 0.55)' },
    { name: 'Light Yellow', value: 'rgba(255, 195, 51, 0.55)' }
  ];
  // Set default to Blue
  const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[1].value);

  // Handler for RUN buttons
  const handleRun = async (idx) => {
    const row = rows[idx];
    if (row.type === 'yog59' || row.type === 'shadashatak') {
      // Unified handler for both 5/9 Yog and 6/8 Yog
      const studyType = row.type === 'yog59' ? '5-9-study' : '6-8-study';
      console.log(`[VedicAspectsStudyDialog] RUN clicked (${studyType}):`, { barData, extendedBarData });
      if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc) || barData.dates.length === 0) {
        alert('Chart data not loaded.');
        return;
      }
      const startDate = barData.dates?.[0];
      const endDate = extendedBarData.dates?.[extendedBarData.dates.length - 1] || barData.dates[barData.dates.length - 1];
      const payload = {
        studyType,
        startDate,
        endDate,
        symbol: barData.symbol || undefined,
        color: selectedColor,
      };
      try {
        const response = await api.post('/api/area-highlight-study/vedic-aspects/yogStudy', payload);
        const data = response.data;
        const { datePairs, summaryLines, futureDates } = data;
        addAreaHighlightOverlaysAndMessages({
        isAdmin,
          datePairs: Array.isArray(datePairs) ? datePairs : [],
          summaryLines: Array.isArray(summaryLines) ? summaryLines : [],
          futureDates: Array.isArray(futureDates) ? futureDates : [],
          params: {
            color: data.color || selectedColor,
            label: `${studyType === '5-9-study' ? '5/9 Yog' : '6/8 Yog'}: Sun`,
            planet1: 'Sun',
            planet2: studyType === '5-9-study' ? 'Cancer' : 'Aries',
          },
          addOverlay,
          barData,
          extendedBarData,
          updateMessagesPanel: typeof onRun === 'function' ? ({ summaryLines, futureDates }) => {
            onRun({ stats: summaryLines, futureDates });
          } : undefined,
          onJournalAppend,
        });
      } catch (err) {
        alert(`Failed to run ${studyType} study: ` + err.message);
      }
      // Placeholder for other studies
    }
  };

  // Render label and dropdown for each row
  const getRowContent = (row, idx) => {
    switch (row.type) {
      case 'nakshatraPlanet':
        return <>
          In Nakshatra of Planet
          <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              {PLANETS.filter(p => p !== row.planet).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </>;
      case 'rashiPlanet':
        return <>
          In-<span style={{ fontWeight: 'bold', marginRight: 4 }}>Rashi</span>-of Planet
          <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              {PLANETS.filter(p => p !== row.planet).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </>;
      case 'drishtiPlanet':
        return <>
          In-<span style={{ fontWeight: 'bold', marginRight: 4 }}>Drishti</span>-of Planet
          <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              {PLANETS.filter(p => p !== row.planet).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </>;
      case 'nakshatraFriend':
        return <>
          In Nakshatra of-<span style={{ fontWeight: 'bold' }}>Friend</span>-Planet
          <FormControl size="small" sx={{ minWidth: 120, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              <MenuItem value="">Select</MenuItem>
              {/* TODO: Populate with Nakshatra options */}
            </Select>
          </FormControl>
        </>;
      case 'nakshatraEnemy':
        return <>
          In Nakshatra of-<span style={{ fontWeight: 'bold' }}>Enemy</span>-Planet
          <FormControl size="small" sx={{ minWidth: 120, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              <MenuItem value="">Select</MenuItem>
              {/* TODO: Populate with Nakshatra options */}
            </Select>
          </FormControl>
        </>;
      case 'shadashatak':
        return <>
          In-<span style={{ fontWeight: 'bold' }}>6/8 Yog</span>-With Planet
          <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              {PLANETS.filter(p => p !== row.planet).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </>;
      case 'yog59':
        return <>
          In-<span style={{ fontWeight: 'bold', marginRight: 4 }}>5/9 Yog</span>-With Planet
          <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
            <Select
  value={PLANETS.includes(row.value) && row.value !== row.planet ? row.value : PLANETS.filter(p => p !== row.planet)[0]}
  onChange={e => handleValueChange(idx, e.target.value)}
  disabled={idx < 5}
>
              {PLANETS.filter(p => p !== row.planet).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </>;
      default:
        return null;
    }
  };


  const handleValueChange = (idx, value) => {
    setRows(prev => prev.map((row, i) => i === idx ? { ...row, value } : row));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.3rem', pb: 1 }}>
        Area Highlight - Vedic Aspects Study
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 2.5, paddingTop: 2.5, minWidth: 680, minHeight: 650 }}>
        <Grid container spacing={2}>
          {rows.map((row, idx) => (
            <Grid item xs={12} key={idx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '100px 180px 1fr 120px',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2.2,
                  minHeight: 56,
                  background: idx % 2 === 0 ? '#f9f9fc' : '#ffffff',
                  borderRadius: 2,
                  px: 1,
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 17, minWidth: 90, pr: 1 }}>
                  Planet
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150, background: '#fff', fontSize: 17 }}>
                  <Select
                    value={row.planet}
                    onChange={e => setRows(prev => prev.map((r, i) => i === idx ? { ...r, planet: e.target.value } : r))}
                    sx={{ fontSize: 17 }}
                    disabled={idx < 5}
                  >
                    {PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, fontSize: 17, width: '100%' }}>
                  {getRowContent(row, idx)}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ px: 4, fontWeight: 'bold', fontSize: 17, height: 44, minWidth: 92, boxShadow: 'none', borderRadius: 1 }}
                    onClick={() => handleRun(idx)}
                    disabled={idx < 5}
                  >
                    RUN
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, mb: 1, minHeight: 56 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 17, minWidth: 90, pr: 1 }}>
            Overlay Color
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160, background: '#fff', fontSize: 17 }}>
            <Select
              value={selectedColor}
              onChange={e => setSelectedColor(e.target.value)}
              sx={{ fontSize: 17 }}
            >
              {COLOR_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box component="span" sx={{ display: 'inline-block', width: 18, height: 18, background: opt.value, borderRadius: '50%', mr: 1, border: '1px solid #ccc', verticalAlign: 'middle' }} />
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ fontWeight: 600, background: '#e3e6ee', color: '#333', borderRadius: 2, boxShadow: 'none', minWidth: 90 }}
            onClick={onClose}
          >
            CLOSE
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

VedicAspectsStudyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }),
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  onRun: PropTypes.func,
  onJournalAppend: PropTypes.func,
};

export default VedicAspectsStudyDialog;
